import {
  createContext,
  useState,
  PropsWithChildren,
  useContext,
  useCallback,
  RefObject
} from 'react';

export enum PROGRESSIVE_TOOLTIP_TYPE {
  LANGUAGES = 'languages'
}

export const TooltipContext = createContext<{
  controllerRef?: RefObject<HTMLDivElement>;
  yAxisCoordinates?: number;
  xAxisCoordinates?: number;
  openedTooltip?: PROGRESSIVE_TOOLTIP_TYPE;
  openTooltip: (tooltipType?: PROGRESSIVE_TOOLTIP_TYPE, controllerRef?: RefObject<HTMLDivElement>) => void;
  closeTooltips: () => void;
}>({
  controllerRef: undefined,
  yAxisCoordinates: undefined,
  xAxisCoordinates: undefined,
  openedTooltip: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  openTooltip: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  closeTooltips: () => {}
});

export const useInitTooltips = () => {
  const [openedTooltip, setOpenedTooltip] = useState<PROGRESSIVE_TOOLTIP_TYPE | undefined>(
    undefined
  );

  const [yAxisCoordinates, setYAxisCoordinates] = useState<number | undefined>(undefined);
  const [xAxisCoordinates, setXAxisCoordinates] = useState<number | undefined>(undefined);

  const [controllerRef, setControllerRef] = useState<RefObject<HTMLDivElement> | undefined>(
    undefined
  );

  const getСoordinates = (ref: RefObject<HTMLDivElement>) => {
    if (ref.current) {
      return ref.current.getBoundingClientRect();
    }
  };

  const openTooltip = useCallback(
    (
      tooltipType?: PROGRESSIVE_TOOLTIP_TYPE,
      controllerRef?: RefObject<HTMLDivElement>
    ) => {
      setOpenedTooltip(tooltipType);
      setYAxisCoordinates(getСoordinates(controllerRef as RefObject<HTMLDivElement>)?.y);
      setXAxisCoordinates(getСoordinates(controllerRef as RefObject<HTMLDivElement>)?.x)
      setControllerRef(controllerRef);
    },
    []
  );

  const closeTooltips = useCallback(() => {
    setOpenedTooltip(undefined);
    setYAxisCoordinates(undefined);
    setXAxisCoordinates(undefined)
    setControllerRef(undefined);
  }, []);

  return { openTooltip, openedTooltip, closeTooltips, yAxisCoordinates, xAxisCoordinates, controllerRef };
};

export const TooltipProvider = ({ children }: PropsWithChildren) => {
  const values = useInitTooltips();
  return <TooltipContext.Provider value={values}>{children}</TooltipContext.Provider>;
};

/**
 * Контекст для управления состоянием тултипов, которые можно открывать в любом  месте на странице
 *
 * @param openTooltip функция, которая открывает нужный тултип(необходимо передавать tooltipType из PROGRESSIVE_TOOLTIP_TYPE и controllerRef(реф на элемент по клику на который открывается тултип)).
 * @param openedTooltip переменная, которая показывает активный в данный момент тултип.
 * @param closeTooltips функция, которая закрывает тултипы и чистит стейт TooltipContext.
 * @param yAxisCoordinates переменная, которая показывает отступ тултипа от верхней границы вьюпорта(см. https://developer.mozilla.org/en-US/docs/Web/API/Element/getBoundingClientRect).
 * @param xAxisCoordinates переменная, которая показывает отступ тултипа от левой границы вьюпорта(см. https://developer.mozilla.org/en-US/docs/Web/API/Element/getBoundingClientRect).
 * @param controllerRef реф на элемент управляющий тултипом.
 */

export const useTooltip = () => {
  return useContext(TooltipContext);
};
